/**
 * PATH: packages/kanban-host/src/api.ts
 * FILE_ID: KANBANHOST-API-001
 * PURPOSE: Tenant-agnostic Kanban API singleton for hubs that talk to
 *          the unified Django backend at `quotech-backend.quotech.ai`.
 *
 * The Kanban API lives in the unified Django backend (see `email_kanban`
 * app + qb!2393). Tenant SPAs that share that backend (currently SAM and
 * Newmark) auth via the cross-subdomain `quotech_auth` JWT cookie issued
 * on `.quotech.ai` — the same pattern used by tenant
 * `shared/services/quotechApi.ts`.
 *
 * This wrapper exists for two reasons:
 *   1. Compose `VITE_QUOTECH_BACKEND` + `/api/kanban` into the base URL
 *      so the primitive doesn't need to know about per-tenant backends.
 *   2. Surface the Quotech bearer header from the cookie, so cards/boards
 *      load with the user's existing SSO session without any extra prompt.
 *
 * Tenants on a different auth model (e.g. local steel-api JWT) can swap
 * the resolver in `getAuthHeaders` without touching the primitive.
 *
 * Historical naming: the exported `getSamKanbanApi` retains its
 * pre-lift name to keep callers stable across tenants. A tenant-agnostic
 * alias (`getKanbanApi`) is exported alongside; new code should prefer
 * the alias.
 */

import {
  createKanbanFetchApi,
  type KanbanApi,
} from '@quotech/ui/kanban'
import {
  attemptSilentRefresh,
  dispatchQuotechAuthLost,
  dispatchQuotechAuthRestored,
  resolveQuotechTenantId,
} from '@quotech/tenant-shared/services/quotechApi'

const QUOTECH_BACKEND =
  (import.meta.env.VITE_QUOTECH_BACKEND as string | undefined) ??
  'https://quotech-backend.quotech.ai'

const QUOTECH_AUTH_COOKIE = 'quotech_auth'

function getQuotechToken(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${QUOTECH_AUTH_COOKIE}=([^;]*)`),
  )
  return match ? decodeURIComponent(match[1]) : null
}

function buildBaseUrl(): string {
  const trimmed = QUOTECH_BACKEND.replace(/\/+$/, '')
  return `${trimmed}/api/kanban`
}

let _instance: KanbanApi | null = null

/**
 * Singleton `KanbanApi` for tenants on the `quotech_auth` cookie auth
 * model. Constructed lazily so import-order doesn't matter (the cookie
 * may not be present during module evaluation in dev).
 *
 * Exported under both `getSamKanbanApi` (original name) and
 * `getKanbanApi` (tenant-agnostic alias). New consumers should prefer
 * the alias; existing callers can migrate at their leisure.
 */
export function getSamKanbanApi(): KanbanApi {
  if (_instance) return _instance
  _instance = createKanbanFetchApi({
    baseUrl: buildBaseUrl(),
    // BE /progressive-snapshot/ is live (change #6 / !4539). Opt-in was
    // required while production returned 404 (BUG-2026-07-16); flip on so
    // cold open uses the bounded priority path instead of full snapshot.
    enableProgressiveSnapshot: true,
    // Explicit `Record<string, string>` return — without it TS infers
    // the union `{Authorization: string} | {Authorization?: undefined}`
    // whose second arm fails the string index signature.
    getAuthHeaders: (): Record<string, string> => {
      const headers: Record<string, string> = {}
      const token = getQuotechToken()
      if (token) headers.Authorization = `Bearer ${token}`
      // Tenant boundary (#249) — carry the ACTIVE master-Quotech org on
      // every kanban request. `getAuthHeaders` is merged by `_request`,
      // `searchKanban`, and `_trelloRequest` alike, so this single seam
      // fences EVERY org-wide read (listBoards / assigned-to-me /
      // due-soon / calendar-actions / search / card-search /
      // quote-stage-summary) at the wire — the master backend resolves
      // org from `X-Organisation-ID` (see the org-scoping notes in
      // `@quotech/api` backend-schema). It is read LIVE per request, so a
      // mid-session org switch is reflected on the next call without
      // rebuilding the singleton. No second source of truth:
      // `resolveQuotechTenantId()` is the exact runtime→buildtime chain
      // `resolveKanbanOrgId` prefers (the runtime slot is seeded from
      // `session.organisation.quotech_org_id` on every /auth/sso + /me).
      const orgId = resolveQuotechTenantId()
      if (orgId != null) headers['X-Organisation-ID'] = String(orgId)
      return headers
    },
    // 2026-05-21 — silent auto-refresh on 401. Fires BEFORE
    // `onResponseError` so the access-cookie-expired path is
    // transparent to the user. Contract: returns `true` if the
    // refresh round-trip succeeded; `_request` then retries the
    // original call once with the freshly-minted cookies in place.
    // Returns `false` if the refresh fails (refresh cookie also
    // dead → user genuinely signed out); the request falls through
    // to `onResponseError` below, which fires the banner.
    //
    // `attemptSilentRefresh` is dedup'd at the module level — the
    // same singleton promise is shared with `quotechFetch`, so a
    // 401 cascade across emailApi + kanban + outlook produces ONE
    // refresh round-trip, not N. See `attemptSilentRefresh` in
    // `@quotech/tenant-shared/services/quotechApi` for the full
    // contract.
    onUnauthorized: attemptSilentRefresh,
    // Fallback observer — fires only when `onUnauthorized` returned
    // `false` (refresh genuinely failed) OR when a non-401 response
    // body was the cause. For 401s post-silent-refresh-failure we
    // bridge to the global `QUOTECH_AUTH_LOST_EVENT` channel so the
    // tenant SPA's `QuotechReconnectBanner` appears. This matches
    // the same fallback the `quotechFetch` path uses post-refresh-
    // failure: the operator only sees the banner when they
    // genuinely need to re-login (refresh cookie expired or
    // revoked).
    onResponseError: (info) => {
      if (info.status === 401) {
        dispatchQuotechAuthLost(`/api/kanban${info.path}`)
      }
    },
    // Self-heal the sticky reconnect banner: the moment a kanban call
    // succeeds again, clear it. On a Kanban-only surface there is no
    // email/outlook poll to provide that recovery signal, so without
    // this the banner could linger after a transient blip even though
    // the board is loading fine (BUG-2026-05-31-RECONNECT-BANNER-
    // STICKY). No-ops unless a banner is actually active.
    onResponseSuccess: dispatchQuotechAuthRestored,
  })
  return _instance
}

/**
 * Tenant-agnostic alias for `getSamKanbanApi`. Prefer this in new code.
 */
export const getKanbanApi = getSamKanbanApi
